import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, CardDescription, CardHeader, CardTitle, Field, Input, toast } from "@dishfolio/ui";
import { api, withOrg } from "../lib/api.ts";
import { Download, Trash2 } from "lucide-react";
import { useOrg } from "../lib/org.tsx";
import { dateTime } from "../lib/format.ts";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { notifyError } from "../components/Upgrade.tsx";

type AuditEntry = { id: string; action: string; resourceType: string; resourceId: string | null; actorUserId: string | null; createdAt: string; metadata: Record<string, unknown> | null };

export function SettingsPage() {
  const { org, refresh } = useOrg();
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState("");
  useEffect(() => setName(org?.organization.name ?? ""), [org]);
  const audit = useQuery({ queryKey: ["audit"], queryFn: () => api.get<{ entries: AuditEntry[] }>("/organization/audit-log") });
  return (
    <PageContainer>
      <PageHeader title="Organization settings" />
      <div className="grid gap-6">
        <Card>
          <CardTitle>Name</CardTitle>
          <form
            className="flex gap-2"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api.patch("/organization", { name });
                await refresh();
                toast.success("Saved");
              } catch (err) {
                notifyError(err);
              }
            }}
          >
            <Field className="flex-1">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Button type="submit">Save</Button>
          </form>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Activity log</CardTitle>
            <CardDescription>Security-relevant changes in your organization (last 200).</CardDescription>
          </CardHeader>
          <ul className="divide-y text-sm">
            {audit.data?.entries.map((e) => (
              <li key={e.id} className="flex flex-wrap justify-between gap-2 py-2">
                <span className="font-mono text-xs">{e.action}</span>
                <span className="text-xs text-muted-foreground">{dateTime(e.createdAt)}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Your data</CardTitle>
            <CardDescription>Menus, orders and team data belong to you. Download a full JSON export at any time.</CardDescription>
          </CardHeader>
          <Button asChild variant="outline" className="justify-self-start">
            <a href={withOrg("/api/v1/organization/export")} download>
              <Download /> Export all data
            </a>
          </Button>
        </Card>
        {org?.role === "owner" ? (
          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle>Delete organization</CardTitle>
              <CardDescription>All menus go offline immediately. Everything — menus, images, orders and analytics — is permanently erased after 30 days. Cancel any paid subscription first.</CardDescription>
            </CardHeader>
            <Field label={`Type “${org.organization.name}” to confirm`}>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </Field>
            <Button
              variant="destructive"
              className="justify-self-start"
              disabled={confirm !== org.organization.name}
              onClick={async () => {
                try {
                  await api.del("/organization", { confirm });
                  localStorage.removeItem("dishfolio.org");
                  window.location.href = "/";
                } catch (err) {
                  notifyError(err);
                }
              }}
            >
              <Trash2 /> Delete organization
            </Button>
          </Card>
        ) : null}
      </div>
    </PageContainer>
  );
}
